import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AppNotification,
    GetNotificationsResponse,
    NotificationCategory,
    NotificationTab,
    UseNotificationsReturn,
} from './NotificationCenter.types';
import { NOTIFICATION_CATEGORY, NOTIFICATION_PAGE_SIZE, NOTIFICATION_TAB } from './NotificationCenter.constants';
import { fetchNotificationsService, markAllNotificationsReadService, markNotificationReadService } from '../../store/investor/service';
import { getPlatformUserId } from '../../utils/utils';

const ALL_CATEGORIES: NotificationCategory[] = [NOTIFICATION_CATEGORY.LEAD, NOTIFICATION_CATEGORY.DEAL];

interface UseNotificationsOptions {
    /** Called after any successful mark-read / mark-all-read so the parent can refresh the unread-count badge. */
    onUnreadCountChange?: () => void;
}

export function useNotifications(panelOpen: boolean, options?: UseNotificationsOptions): UseNotificationsReturn {
    const organisationUserId = getPlatformUserId();
    const [allNotifications, setAllNotifications] = useState<AppNotification[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [activeTab, setActiveTab] = useState<NotificationTab>(NOTIFICATION_TAB.ALL);
    const [selectedCategories, setSelectedCategories] = useState<Set<NotificationCategory>>(
        () => new Set([NOTIFICATION_CATEGORY.LEAD, NOTIFICATION_CATEGORY.DEAL]),
    );

    // Mutable refs for paging state — avoid stale closures in concurrent loadMore calls
    // and let loadFirstPage reset deterministically.
    const nextPageRef = useRef(0);
    const totalCountRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);
    // Keep callback in a ref so identity changes don't invalidate markRead/markAllRead deps.
    const onUnreadCountChangeRef = useRef(options?.onUnreadCountChange);
    onUnreadCountChangeRef.current = options?.onUnreadCountChange;

    const fetchPage = useCallback(
        async (pageNum: number): Promise<{ items: AppNotification[]; totalCount: number } | null> => {
            if (!organisationUserId) return null;
            try {
                const res = await fetchNotificationsService({ organisationUserId, pageNum, pageSize: NOTIFICATION_PAGE_SIZE });
                const data: GetNotificationsResponse = res.data?.responseData ?? res.data;
                return { items: data.notifications ?? [], totalCount: data.totalCount ?? 0 };
            } catch {
                return null;
            }
        },
        [organisationUserId],
    );

    const loadFirstPage = useCallback(async () => {
        if (!organisationUserId || inFlightRef.current) return;
        inFlightRef.current = true;
        setLoading(true);
        const res = await fetchPage(0);
        if (!res) {
            setLoading(false);
            inFlightRef.current = false;
            return;
        }
        setAllNotifications(res.items);
        totalCountRef.current = res.totalCount;
        nextPageRef.current = 1;
        // hasMore if server returned a full page AND totalCount says more remain
        const moreByPageFull = res.items.length >= NOTIFICATION_PAGE_SIZE;
        const moreByTotal = res.items.length < res.totalCount;
        setHasMore(moreByPageFull && moreByTotal);
        setLoading(false);
        inFlightRef.current = false;
    }, [organisationUserId, fetchPage]);

    const loadMore = useCallback(async () => {
        if (!organisationUserId || inFlightRef.current || !hasMore) return;
        inFlightRef.current = true;
        setLoadingMore(true);
        const pageToLoad = nextPageRef.current;
        const res = await fetchPage(pageToLoad);
        if (!res) {
            setLoadingMore(false);
            inFlightRef.current = false;
            return;
        }
        setAllNotifications((prev) => {
            // De-dupe by id in case the server delivers overlapping pages (e.g., new notifications inserted)
            const existing = new Set(prev.map((n) => n.id));
            const appended = res.items.filter((n) => !existing.has(n.id));
            const merged = [...prev, ...appended];
            totalCountRef.current = res.totalCount;
            const noNewItems = appended.length === 0;
            const fewerThanPage = res.items.length < NOTIFICATION_PAGE_SIZE;
            const reachedTotal = merged.length >= res.totalCount;
            setHasMore(!noNewItems && !fewerThanPage && !reachedTotal);
            return merged;
        });
        nextPageRef.current = pageToLoad + 1;
        setLoadingMore(false);
        inFlightRef.current = false;
    }, [organisationUserId, fetchPage, hasMore]);

    useEffect(() => {
        if (panelOpen) {
            loadFirstPage();
        } else {
            // Reset so a re-open starts at page 0 — also prevents stale list flicker
            setAllNotifications([]);
            nextPageRef.current = 0;
            totalCountRef.current = null;
            setHasMore(false);
        }
    }, [panelOpen, loadFirstPage]);

    const visibleNotifications = useMemo(() => {
        const categoryFilterInactive = selectedCategories.size === 0 || selectedCategories.size === ALL_CATEGORIES.length;

        const passesTabFilter = (n: AppNotification): boolean => {
            switch (activeTab) {
                case NOTIFICATION_TAB.UNREAD:
                    return !n.isRead;
                case NOTIFICATION_TAB.MENTIONS:
                    return n.isMention;
                default:
                    return true;
            }
        };

        const passesCategoryFilter = (n: AppNotification): boolean => {
            if (categoryFilterInactive) return true;
            if (n.category == null) return true;
            return selectedCategories.has(n.category);
        };

        return allNotifications.filter((n) => passesTabFilter(n) && passesCategoryFilter(n));
    }, [allNotifications, activeTab, selectedCategories]);

    const markAllRead = async () => {
        if (!organisationUserId) return;
        await markAllNotificationsReadService({ organisationUserId });
        setAllNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        onUnreadCountChangeRef.current?.();
    };

    const markRead = async (notificationId: string) => {
        if (!organisationUserId) return;
        try {
            await markNotificationReadService({ organisationUserId, notificationId });
            setAllNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)));
            onUnreadCountChangeRef.current?.();
        } catch {
            // keep state on failure
        }
    };

    return {
        notifications: visibleNotifications,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        activeTab,
        setActiveTab,
        markAllRead,
        markRead,
        selectedCategories,
        setSelectedCategories,
    };
}
